// UserDetailsPage.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Avatar, Button, Popconfirm, Row, Col, Tabs } from 'antd';
import { EditFilled, DeleteFilled, LockFilled, UnlockFilled, UserOutlined, ReloadOutlined } from '@ant-design/icons';
import styles from './UserDetailsPage.module.scss';
import UserService from '../../services/UserService';
import UserGroupes from '../../components/groupe/UserGroupes';
import LoadingPlaceholder from '../../components/utils/LoadingPlaceholder';
import PieceJointeList from '../../components/piecejointe/PieceJointeList';
import ActivityFeed from "../../components/Activity/ActiviteFeed";
import { catchError } from '../../services/DaoService';
import Toast from '../../helpers/Toast';
import DisableUserModal from '../../components/user/DisableUserModal';
import UserEdit from '../../components/user/UserEdit';
import PageTitle from '../../components/utils/PageTitle';
import LogTraces from '../../components/logtrace/LogTraces';
import Main from '../../components/layouts/Main';
import { useUserContext } from '../../contexts/UserContext';
import Breadcrumb from '../../components/utils/Breadcrumb';

const UserDetailsPage = () => {
    const { uid } = useParams();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const { check } = useUserContext();
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [isUserStateChanging, setIsUserStateChanging] = React.useState(false);
    const [disabledModalVisible, setDisabledModalVisible] = React.useState(false);
    const [updateModalVisible, setUpdateModalVisible] = React.useState(false);
    const navigate = useNavigate();

    const init = useCallback(() => {
        setLoading(true);
        UserService.getInstance()
            .find(uid)
            .then(response => {
                setUser(response.data);
            }).catch(error => {
                console.error(error);
            }).finally(() => {
                setLoading(false);
            });
    }, [uid]);

    useEffect(() => {
        if (!check('VIEW-USER')) {
            return;
        }
        init();
    }, [init, check]);

    const openEditModal = () => {
        setUpdateModalVisible(true);
    };

    const openDisabledModal = () => {
        setDisabledModalVisible(true);
    }

    const handleDisable = (user) => {
        setUser(user);
        setDisabledModalVisible(false);
    };

    const handleEnable = (user) => {
        setIsUserStateChanging(true);
        UserService.getInstance()
            .enable(user.uid)
            .then((response) => {
                Toast.success(response.message);
                setUser(response.data);
            }).catch((error) => {
                catchError(error);
            })
            .finally(() => {
                setIsUserStateChanging(false);
            });
    };

    const handleUpdate = (user) => {
        setUser(user);
        setUpdateModalVisible(false);
    }

    const handleDelete = (user) => {
        setIsDeleting(true);
        UserService.getInstance().remove(user.uid)
            .then(() => {
                navigate(`/user`);
            }).catch(error => {
                catchError(error);
            }).finally(() => {
                setIsDeleting(false);
            });
    };

    const refreshUser = () => {
        init();
    };

    const tabItems = [
        check('VIEW-USER-ATTACHMENTS') && {
            key: "attachments",
            label: "Pièces jointes",
            children: user ? <PieceJointeList canSee={!!check('VIEW-USER-ATTACHMENTS')}
                canAdd={!!check('ADD-USER-ATTACHMENTS')} candRemove={!!check('DELETE-USER-ATTACHMENTS')}
                parentType={UserService.CLASS_NAME} parentId={user.id} /> : <LoadingPlaceholder />
        },
        check('VIEW-USER-ATTACHMENTS') && {
            key: "Activite",
            label: "Activités",
            children: user ? <ActivityFeed canSee={!!check('VIEW-USER-ATTACHMENTS')}
                parentType={UserService.CLASS_NAME} parentId={user.id}
            /> : <LoadingPlaceholder />
        },
        check('MANAGE-DATA-OPERATIONS-HISTORY') && user && {
            key: "trace-logs",
            label: "Logs",
            children: user ? <LogTraces target={'user'} parentId={user?.id} /> : <LoadingPlaceholder />
        },
    ];

    return (
        <Main>
            <Breadcrumb parentLink="/user" canSee={check('VIEW-USERS')} parentLinkTitle="Liste des utilisateurs" title={user?.name ?? 'En chargement...'} />
            <PageTitle title={user ? user.name : 'Utilisateur'} icon={<UserOutlined />}
                subTitle={user?.email} backPageTitle="Liste des utilisateurs"
                backPageLink={"/user"} canSeeBackPageLink={check('VIEW-USERS')}>
                {check('EDIT-USER') ?
                    <Button onClick={() => openEditModal(user)} type="primary" className='btn btn-warning' icon={<EditFilled />}>Éditer</Button>
                    : null}
                {user?.enabled ? (
                    check('DISABLE-USER') ? (
                        <Button disabled={user?.organisation && !user?.organisation?.accepted} loading={isUserStateChanging} onClick={() => openDisabledModal(user)} type="primary" danger icon={<LockFilled />}>Désactiver</Button>
                    ) : <></>
                ) : (
                    check('ENABLE-USER') ? (
                        <Popconfirm title="Voulez-vous vraiment activer cet utilisateur ?" onConfirm={() => handleEnable(user)} okText="Activer" cancelText="Annuler" okType='primary'>
                            <Button disabled={user?.organisation && !user?.organisation?.accepted} type='primary' loading={isUserStateChanging} icon={<UnlockFilled title='Activer un utilisateur' />}>Activer</Button>
                        </Popconfirm>
                    ) : <></>
                )}
                {check('DELETE-USER') ?
                    <Popconfirm title="Voulez-vous vraiment supprimer cet utilisateur?" onConfirm={() => handleDelete(user)} okText="Oui" okButtonProps={{ danger: true }} cancelText="Non">
                        <Button disabled={user?.organisation && !user?.organisation?.accepted} type="primary" loading={isDeleting} danger icon={<DeleteFilled />}>Supprimer</Button>
                    </Popconfirm>
                    : null}
                <Button onClick={refreshUser} type="default" icon={<ReloadOutlined />} loading={loading}>Rafraîchir</Button>
            </PageTitle>
            {loading ? (
                <LoadingPlaceholder />
            ) :
                <Row gutter={[5, 5]}>
                    <Col span={24}>
                        <Card loading={loading} className={styles.card} classNames={{ body: "p-1" }}>
                            <div className={styles.header}>
                                <Avatar size={100} src={user.avatar} className={styles.avatar} />
                                <div className={styles.info}>
                                    <h1 className={styles.name}>{user.name}</h1>
                                    <p className={styles.contacts}>{user.email}
                                        <br />
                                        {user.telephone}
                                        <br />
                                        {user.enabled ? (
                                            <span className="badge bg-success">Actif</span>
                                        ) : <span className="badge bg-danger p-2 fs-6">Inactif</span>}
                                    </p>
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col sm={24} md={12}>
                        <UserGroupes user={user} />
                    </Col>
                    <Col sm={24} md={12}>
                        <Card>
                            <Tabs items={tabItems} />
                        </Card>
                    </Col>
                </Row>
            }
            {user ? <UserEdit
                visible={updateModalVisible}
                uid={user.uid}
                onUpdate={handleUpdate}
                onCancel={() => setUpdateModalVisible(false)}
            /> : null}
            {user ? <DisableUserModal
                visible={disabledModalVisible}
                uid={user.uid}
                onUpdate={handleDisable}
                onCancel={() => setDisabledModalVisible(false)}
            /> : null}
        </Main>
    );
};

export default UserDetailsPage;